import type { ParentCaseInfo } from '../types/stepNodes';

/**
 * Converts behavior branch metadata into natural scheduling copy.
 *
 * Example outputs:
 * - reply to Welcome Email
 * - reply to text
 * - link click on Newsletter
 * - timeout for Welcome Email
 */
export function getBehaviorScheduleAnchorDescription(parentCaseInfo?: ParentCaseInfo): string | undefined {
	if (!parentCaseInfo?.isBehaviorCase || !parentCaseInfo.caseName) {
		return undefined;
	}

	const normalizedCaseName = parentCaseInfo.caseName.replace(/^if\s+/i, '').trim();
	if (!normalizedCaseName) {
		return undefined;
	}

	const sourceStepName = parentCaseInfo.sourceStepName?.trim();
	const withSource = (base: string, relation: string) =>
		sourceStepName ? `${base} ${relation} ${sourceStepName}` : base;

	if (/timeout/i.test(normalizedCaseName) || /^no\s+engagement/i.test(normalizedCaseName)) {
		return withSource('timeout', 'for');
	}

	if (/repl(?:y|ies|ied)/i.test(normalizedCaseName)) {
		if (sourceStepName) {
			return `reply to ${sourceStepName}`;
		}

		return /text/i.test(normalizedCaseName) ? 'reply to text' : 'reply';
	}

	if (/click/i.test(normalizedCaseName)) {
		const clickDescription = /link/i.test(normalizedCaseName) ? 'link click' : 'click';
		return withSource(clickDescription, 'on');
	}

	if (/open/i.test(normalizedCaseName)) {
		return sourceStepName ? `opening of ${sourceStepName}` : 'open';
	}

	return sourceStepName
		? `${normalizedCaseName.charAt(0).toLowerCase() + normalizedCaseName.slice(1)} for ${sourceStepName}`
		: normalizedCaseName.charAt(0).toLowerCase() + normalizedCaseName.slice(1);
}
